import React, { useEffect, useMemo } from 'react';
import { Box, Card, Typography, LinearProgress, Skeleton } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NoDataFound from '../../../../../components/NodataIndication/NoDataFound';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getSogodashboardCards } from '../../../../../redux/features/poinTrackerSlice';

export default function DashboardCards({ fromDate, toDate, selectedTeam }) {
  const dispatch = useDispatch();
  const { loading, responseData } = useSelector((state) => state.pointTracker.sogoDashboardCards);

  const dispatchParams = useMemo(() => {
    return {
      fromDate,
      toDate,
      wingId: selectedTeam?.id === -1 ? null : selectedTeam?.id,
    };
  }, [fromDate, toDate, selectedTeam?.id]);

  useEffect(() => {
    if (fromDate && toDate) {
      dispatch(getSogodashboardCards(dispatchParams));
    }
  }, [dispatch, dispatchParams]);

  const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;
  const cardsData = responseData?.data ?? {};
  const data = {
    totalSales: cardsData?.totalSales?.value || 0,
    trends: cardsData?.totalSales?.trend || 0,
    nextTarget: cardsData?.nextTarget?.value || 0,
    progress: cardsData?.nextTarget?.progress || 0,
    balance: cardsData?.progress?.value || 0,
  };

  // const noDataFound = !loading && data.totalSales === 0 && data.nextTarget === 0 && data.balance === 0;

  return (
    <Box sx={gridStyle}>
      {loading ? (
        // 🔁 SKELETON UI
        [...Array(4)].map((_, i) => (
          <Card key={i} sx={cardStyle}>
            <Skeleton width="40%" height={20} />
            <Skeleton width="60%" height={35} sx={{ mt: 1 }} />
            <Skeleton width="50%" height={20} sx={{ mt: 1 }} />
            <Skeleton variant="rectangular" height={6} sx={{ mt: 1, borderRadius: 2 }} />
          </Card>
        ))
      ) : (
        <>
          {/* TOTAL SALES */}
          <Card sx={cardStyle}>
            <Typography variant="subtitle2">TOTAL SALES</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
              {formatCurrency(data.totalSales)}
            </Typography>
            <Typography
              sx={{
                color: data.trends >= 0 ? 'green' : 'red',
                mt: 1,
                fontSize: 14,
              }}
            >
              {data.trends >= 0 ? '↑' : '↓'} {Math.abs(data.trends)}% vs Last Month
            </Typography>{' '}
          </Card>

          {/* NEXT TARGET */}
          <Card sx={cardStyle}>
            <Typography variant="subtitle2">NEXT TARGET</Typography>

            <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
              {formatCurrency(data.nextTarget)}
            </Typography>

            <Typography sx={{ fontSize: 13, mt: 1 }}>Progress</Typography>

            <LinearProgress
              variant="determinate"
              value={data.progress}
              sx={{
                height: 6,
                borderRadius: 5,
                mt: 1,
              }}
            />

            <Typography sx={{ fontSize: 13, mt: 0.5 }}> {data.progress}%</Typography>
          </Card>

          {/* BALANCE */}
          <Card sx={cardStyle}>
            <Typography variant="subtitle2">BALANCE</Typography>

            <Typography variant="h5" sx={{ fontWeight: 700, mt: 1, color: 'red' }}>
              {formatCurrency(data.balance)}
            </Typography>
          </Card>

          {/* SUBMISSIONS (GRADIENT CARD) */}
          <Card
            sx={{
              p: 2,
              borderRadius: 3,
              color: '#fff',
              background: 'linear-gradient(135deg, #7b1fa2, #e91e63)',
              position: 'relative',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="subtitle2">Submissions</Typography>
              <EmojiEventsIcon />
            </Box>

            {/* Current Slot */}
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: 13 }}>Current Slot</Typography>
              <Typography sx={{ fontSize: 13, textAlign: 'right' }}>75%</Typography>

              <LinearProgress
                variant="determinate"
                value={75}
                sx={{
                  height: 6,
                  borderRadius: 5,
                  mt: 0.5,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#fff',
                  },
                }}
              />
            </Box>

            {/* Next Slot */}
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: 13 }}>Next Slot</Typography>
              <Typography sx={{ fontSize: 13, textAlign: 'right' }}>60%</Typography>

              <LinearProgress
                variant="determinate"
                value={60}
                sx={{
                  height: 6,
                  borderRadius: 5,
                  mt: 0.5,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#ffeb3b',
                  },
                }}
              />
            </Box>
          </Card>
        </>
      )}
    </Box>
  );
}

//* Styles */
const gridStyle = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: '1fr 1fr',
    md: '1fr 1fr 1fr 1fr',
  },
  gap: 2,
  p: 2,
};

const cardStyle = {
  p: 2,
  borderRadius: 3,
  boxShadow: 2,
};
